"use client";

import { useEffect } from "react";
import { reportClientError } from "@/lib/error-log/client";

/**
 * Next.js root-level error boundary. Catches uncaught render errors that
 * escape every segment boundary, including those that crash the root
 * layout. Must render its own <html>/<body> because the root is gone.
 *
 * Logs the error to the in-app error log on mount, then renders a
 * minimal "something broke" fallback. The user can click Try again to
 * recover (which is just `reset()`); usually they'll navigate away.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    reportClientError(error, {
      source: "server",
      errorType: error.name ?? "GlobalError",
      context: error.digest ? { digest: error.digest, boundary: "global" } : { boundary: "global" },
    });
  }, [error]);

  return (
    <html lang="en">
      <body
        style={{
          margin: 0,
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#fcfcfd",
          fontFamily: "ui-sans-serif, system-ui, sans-serif",
          color: "#111827",
          padding: 24,
        }}
      >
        <div style={{ maxWidth: 420, textAlign: "center" }}>
          <h1 style={{ fontSize: 22, margin: "0 0 8px", fontWeight: 600 }}>
            Something broke
          </h1>
          <p style={{ fontSize: 14, color: "#6b7280", margin: "0 0 20px", lineHeight: 1.55 }}>
            The error has been logged. You can try again, or reload the page.
          </p>
          <button
            type="button"
            onClick={reset}
            style={{
              padding: "8px 18px",
              fontSize: 13,
              fontWeight: 500,
              background: "#0e7490",
              color: "white",
              border: "none",
              borderRadius: 8,
              cursor: "pointer",
            }}
          >
            Try again
          </button>
        </div>
      </body>
    </html>
  );
}
